<template>
  <div @click="$emit('click')" class="analysis-card">
    <div v-if="!display.smAndDown" class="analysis-card__date">
      <span class="analysis-card__date--day">
        {{
          getDay(
            analysis.status === 'SCHEDULED'
              ? analysis.appointmentDateTime
              : analysis.analysisDateTime
          )
        }}
      </span>
      <span class="analysis-card__date--year">
        {{
          getYear(
            analysis.status === 'SCHEDULED'
              ? analysis.appointmentDateTime
              : analysis.analysisDateTime
          )
        }}
      </span>
    </div>
    <div class="analysis-card__description">
      <p v-if="display.smAndDown" class="analysis-card__description--date">
        {{
          getFullDate(
            analysis.status === 'SCHEDULED'
              ? analysis.appointmentDateTime
              : analysis.analysisDateTime
          )
        }}
      </p>
      <p>{{ getTitle(analysis) }}</p>
    </div>
    <div class="analysis-card__status">
      <EllipseIcon
        :style="{
          color: analysis.status === 'READY' ? 'green' : 'orange'
        }"
      />
      <p v-if="!display.smAndDown" v-html="getAnaliseStatus(analysis)" />
    </div>
    <div class="analysis-card__download">
      <div
        @click.stop.prevent="
          isDiagnostic
            ? downloadFile(analysis)
            : analysis.results.map((item) => downloadFile(item))
        "
        class="analysis-card__download--wrap"
        :class="[
          isDisabledLink
            ? 'analysis-card__download--wrap--disabled'
            : 'analysis-card__download--wrap--hover'
        ]"
      >
        <DownloadIcon class="analysis-card__download--icon" />
        <span v-if="!display.smAndDown">Скачать</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'

import ru from 'date-fns/locale/ru'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'

import DownloadIcon from '@/assets/icons/download'
import EllipseIcon from '@/assets/icons/ellipse'

import { useDisplay } from '@/hooks'
import { useStore } from '@/store'

import { AnalysisTypes, PaymentStatusTypes } from '@/core/constants'
import { baseURL } from '@/config/request'
import { TRACKER_SLUGS } from '@/models/tracker'

export default {
  name: 'AnalysisListCard',
  components: {
    EllipseIcon,
    DownloadIcon
  },
  props: ['analysis', 'isDiagnostic'],
  setup(props) {
    const display = useDisplay()
    const store = useStore()

    const getAccountInfo = store.getters['account/getAccountInfo']
    const getDiagnosticsProxyData =
      store.getters['diagnostics/getDiagnosticsProxyData']

    const isDisabledLink = computed(() => {
      if (props.isDiagnostic) {
        if (!props.analysis.result) return true
      } else {
        if (props.analysis.status !== 'READY') return true
        if (!props.analysis.results) return true
        if (!props.analysis.results.length) return true
        if (!props.analysis.patientId) return true
      }

      return false
    })

    function getDay(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMM', { locale: ru })
    }

    function getYear(date) {
      if (!date) return ''
      return format(parseISO(date), 'yyy')
    }

    function getFullDate(date) {
      if (!date) return ''
      return `${format(parseISO(date), 'dd MMMM', {
        locale: ru
      })} ${getYear(date)}`
    }

    function getTitle(analysis) {
      if (props.isDiagnostic) {
        const speciality =
          getDiagnosticsProxyData?.specialities[analysis.specialityId]
        return speciality ? speciality.title : ''
      }

      return analysis.services.map(({ title }) => title).join(', ')
    }

    function getAnaliseStatus(analysis) {
      if (props.isDiagnostic) return 'Готово'
      return `${AnalysisTypes[analysis.status]},<br />${
        PaymentStatusTypes[analysis.paymentStatus]
      }`
    }

    function downloadFile(result) {
      if (isDisabledLink.value) return

      const fileId = result.id
      let url

      if (props.isDiagnostic) {
        this.$tracker(TRACKER_SLUGS.SHOW_DIAGNOSTIC_RESULT)
        url = `${baseURL}diagnostics/results/${fileId}/getFile?accountId=${getAccountInfo.id}&patientId=${props.analysis.patientId}`
      } else {
        this.$tracker(TRACKER_SLUGS.SHOW_ANALYSES_RESULT)
        url = `${baseURL}analyses/results/${fileId}/getFile?analysisId=${props.analysis.id}&patientId=${props.analysis.patientId}&accountId=${getAccountInfo.id}`
      }

      const linkEl = document.createElement('a')

      if (fileId) {
        linkEl.download = fileId
      }

      linkEl.style.display = 'none'
      linkEl.href = url

      document.body.appendChild(linkEl)
      linkEl.click()
      document.body.removeChild(linkEl)
    }

    return {
      display,
      getDay,
      getYear,
      getAnaliseStatus,
      getFullDate,
      getTitle,
      downloadFile,
      isDisabledLink
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'style.scss';
</style>
